import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useProducts } from '../hooks/useQueries';
import './Cart.css';

const Cart = () => {
  const {
    items,
    removeItem,
    updateQty,
    toggleGiftWrap,
    subtotal,
    giftWrapTotal,
    total,
    totalItems,
  } = useCart();
  const { data: allProducts = [] } = useProducts();
  const navigate = useNavigate();

  // Pick up to 4 products not already in cart, shuffled for variety
  const cartIds = new Set(items.map(i => i.id));
  const upsellProducts = allProducts
    .filter(p => !cartIds.has(p.id))
    .sort(() => Math.random() - 0.5)
    .slice(0, 4);

  if (items.length === 0) {
    return (
      <div className="cart-page">
        <div className="breadcrumb-bar">
          <div className="container">
            <div className="breadcrumb">
              <Link to="/">Home</Link><span>›</span><span>Cart</span>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="empty-state">
            <div className="empty-state-icon">🛒</div>
            <h3>Your cart is empty</h3>
            <p>Looks like you haven't added any gifts yet. Start exploring!</p>
            <Link to="/products" className="btn btn-primary-warm btn-lg">Browse Gifts</Link>
          </div>
        </div>
      </div>
    );
  }

  // Cart display total = items subtotal + gift wrap (delivery added at checkout)
  const cartTotal = subtotal + giftWrapTotal;

  return (
    <div className="cart-page">
      <div className="breadcrumb-bar">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link><span>›</span><span>Cart ({totalItems} items)</span>
          </div>
        </div>
      </div>

      <div className="container">
        <h1 className="cart-title">Your Cart</h1>
        <div className="cart-layout">
          {/* Items */}
          <div className="cart-items">
            {/* Header */}
            <div className="cart-items-header">
              <span className="col-product">Product</span>
              <span className="col-price">Price</span>
              <span className="col-qty">Quantity</span>
              <span className="col-total">Total</span>
              <span />
            </div>

            {items.map((item) => {
              // Resolve gift wrap addon for this item (if any)
              const giftWrapAddon = (item.addons || []).find(
                a => a.slug === 'gift-wrapping' && a.product_addons?.is_enabled === true
              ) ?? null;
              const giftWrapPrice = giftWrapAddon
                ? Number(giftWrapAddon.product_addons?.custom_price ?? giftWrapAddon.price ?? 0)
                : 0;

              return (
                <div key={item.cartItemId} className="cart-item">
                  <div className="cart-item__product">
                    <Link to={`/products/${item.id}`} className="cart-item__img-wrap">
                      <img src={item.image || item.images[0]} alt={item.name} className="cart-item__img" />
                    </Link>
                    <div className="cart-item__info">
                      <Link to={`/products/${item.id}`} className="cart-item__name">{item.name}</Link>
                      {item.variant && item.variant !== 'Default' && (
                        <p className="cart-item__variant">
                          {item.selected_variant_records?.length > 1
                            ? item.selected_variant_records.map(r =>
                                `${r.product_variant_type?.name || r.variant_type?.name || 'Option'}: ${r.variant_name || r.variant_value}`
                              ).join(' · ')
                            : item.selected_variant_record
                              ? `${item.selected_variant_record.product_variant_type?.name || item.selected_variant_record.variant_type?.name || 'Option'}: ${item.selected_variant_record.variant_name || item.selected_variant_record.variant_value}`
                              : item.variant
                          }
                        </p>
                      )}

                      {/* Variant price-adjustment note — only shown when the selected variant costs extra */}
                      {item.variantPriceAdjustment > 0 && (
                        <p className="cart-item__variant" style={{ fontSize: 12, color: 'var(--primary)' }}>
                          ₹{item.basePrice.toLocaleString('en-IN')} + ₹{item.variantPriceAdjustment.toLocaleString('en-IN')} for selected option
                        </p>
                      )}

                      {/* Customisation fields */}
                      {item.customizations_payload?.length > 0 && (
                        <div style={{ marginTop: 2 }}>
                          {item.customizations_payload.map(cf => {
                            const displayVal = Array.isArray(cf.value) ? cf.value.join(', ') : cf.value;
                            return (
                              <p key={cf.field_id} className="cart-item__variant" style={{ fontSize: 12 }}>
                                {cf.field_label}:{' '}
                                <span style={{ color: 'var(--text, #333)' }}>{displayVal}</span>
                                {Number(cf.price_adjustment) > 0 && (
                                  <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
                                    {' '}(+₹{Number(cf.price_adjustment).toLocaleString('en-IN')})
                                  </span>
                                )}
                              </p>
                            );
                          })}
                        </div>
                      )}

                      {/* Gift wrap toggle — only shown when the product supports it */}
                      {giftWrapAddon && (
                        <label
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            marginTop: 8,
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!item.giftWrap}
                            onChange={() => toggleGiftWrap(item.cartItemId)}
                            style={{ accentColor: 'var(--primary)', width: 14, height: 14, cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            🎀 Gift Wrapping{' '}
                            {giftWrapPrice === 0 ? (
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>FREE</span>
                            ) : (
                              <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
                                +₹{giftWrapPrice.toLocaleString('en-IN')}
                              </span>
                            )}
                          </span>
                        </label>
                      )}

                      <button
                        className="cart-item__remove-mobile"
                        onClick={() => removeItem(item.cartItemId)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className="col-price cart-item__price">₹{item.price.toLocaleString()}</div>

                  <div className="col-qty">
                    <div className="qty-input">
                      <button className="qty-btn" onClick={() => updateQty(item.cartItemId, item.qty - 1)}>−</button>
                      <span className="qty-num">{item.qty}</span>
                      <button className="qty-btn" onClick={() => updateQty(item.cartItemId, item.qty + 1)}>+</button>
                    </div>
                  </div>

                  <div className="col-total cart-item__total">
                    ₹{(item.price * item.qty).toLocaleString()}
                  </div>

                  <button
                    className="cart-item__remove"
                    onClick={() => removeItem(item.cartItemId)}
                    aria-label="Remove"
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              );
            })}

            {/* Coupon */}
            <div className="cart-coupon">
              <input type="text" placeholder="Enter coupon code (e.g. GIFT20)" className="coupon-input" />
              <button className="btn btn-outline btn-sm">Apply</button>
            </div>

            <div className="cart-actions">
              <Link to="/products" className="btn btn-outline">← Continue Shopping</Link>
            </div>
          </div>

          {/* Summary */}
          <div className="cart-summary">
            <h2 className="cart-summary__title">Order Summary</h2>

            <div className="cart-summary__rows">
              <div className="summary-row">
                <span>Subtotal ({totalItems} items)</span>
                <span>₹{subtotal.toLocaleString()}</span>
              </div>

              {/* Gift Wrapping rows — one row per item that has gift wrap enabled */}
              {items.filter(i => i.giftWrap).map(i => {
                const addon = (i.addons || []).find(
                  a => a.slug === 'gift-wrapping' && a.product_addons?.is_enabled === true
                );
                const unitPrice = addon
                  ? Number(addon.product_addons?.custom_price ?? addon.price ?? 0)
                  : 0;
                const lineTotal = unitPrice * i.qty;
                return (
                  <div key={`giftwrap-${i.cartItemId}`} className="summary-row summary-row--gift">
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <span>🎀 Gift Wrapping</span>
                      <span style={{ fontSize: 11, color: 'var(--text-light)' }}>
                        ₹{unitPrice.toLocaleString('en-IN')} × {i.qty} {i.qty > 1 ? 'pcs' : 'pc'}
                      </span>
                    </span>
                    <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
                      ₹{lineTotal.toLocaleString('en-IN')}
                    </span>
                  </div>
                );
              })}

              <div className="summary-divider" />
              <div className="summary-row summary-row--total">
                <span>Total</span>
                <span>₹{cartTotal.toLocaleString()}</span>
              </div>
              <p className="summary-tax">Delivery charges are selected at checkout · Inclusive of all taxes</p>
            </div>

            <button
              className="btn btn-primary-warm btn-lg"
              style={{ width: '100%', marginBottom: 12 }}
              onClick={() => navigate('/checkout')}
            >
              Proceed to Checkout →
            </button>

            <div className="cart-summary__secure">
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>Secure & Encrypted Checkout</span>
            </div>

            <div className="cart-summary__payments">
              {['Visa', 'MC', 'UPI', 'GPay', 'NB'].map(p => (
                <span key={p} className="payment-icon">{p}</span>
              ))}
            </div>
          </div>
        </div>

        {/* You may also like */}
        {upsellProducts.length > 0 && (
          <div className="cart-upsell">
            <h3 className="cart-upsell__title">You may also like</h3>
            <div className="cart-upsell__grid">
              {upsellProducts.map(p => (
                <Link key={p.id} to={`/products/${p.id}`} className="upsell-card">
                  <img src={p.images?.[0]} alt={p.name} />
                  <p className="upsell-card__name">{p.name}</p>
                  <p className="upsell-card__price">₹{Number(p.price).toLocaleString('en-IN')}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;